import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPriceParts } from "../../../lib/currency";
import { currencyLabel } from "../../../lib/format";
import { formatStayMoney } from "../lib/money";
import { parseChildAges } from "../lib/nights";
import { ALL_STAY_TYPES, useStayDestinations, useStaysByType } from "../api/stays-query";
import { sortStayProperties, type StaysCitySortOption } from "../lib/sort-stay-properties";
import { StayCard } from "./stay-card";
import { StaysSearching } from "./stays-searching";
import { ActivityCardDiscover } from "../../activities/components/activity-card-discover";
import { activityHrefString } from "../../activities/lib/activity-href";
import { nearbyLandmark } from "../lib/landmarks";
import { STAY_VIBES, VIBE_ICON, campHasVibes, stayHasVibes, type StayVibe } from "../lib/vibes";
import { barLine } from "./stays-sticky-bar";
import { countStayFilters, inPriceRange, matchesStayFilters, type StayFilters } from "../lib/stay-filters";
import { nightsBetween, useNightlyPrice } from "../lib/nightly";
import { StaysStickyBar } from "./stays-sticky-bar";
import { MoodGlyph } from "./mood-glyph";
import { MapViewStyles } from "./map-view-styles";
import { StaysPhoneMap, type PhoneMapStay } from "./stays-phone-map";
import { SaveHeart, type SaveTarget } from "../../saved/components/save-heart";
import { pewterInner } from "../../../shared/ui/pewter-edge";
import { CARD_FILL, CARD_RADIUS, cardFrameStyle } from "../../../shared/ui/card-frame";
import { STAY_ICON_VERSION, stayTypeIconSrc } from "../lib/stay-type-icon";

/** Camps are an activity category; their clay icon lives beside the type ones. */
const CAMP_ICON = `/images/subcategories/sub-camp.png?v=${STAY_ICON_VERSION}`;
import { STAYS_EASE, glideCards, measureCards, staggerIn, visibleCards } from "../lib/motion";
import { StaysHubMapLazy, preloadStaysMap, type StaysMapBounds, type StaysMapPin } from "./stays-hub-map-lazy";
import { useLoadOnScroll } from "../../../shared/hooks/use-load-on-scroll";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

const PAGE = 24;
const NAVY = "#110E39";
const META = "#5a5d77";

interface CampItem {
  isNew?: boolean;
  id: string;
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
  segment?: string | null;
  mode?: string;
  latitude?: number | null;
  longitude?: number | null;
}

/** One stay as the map and the phone's tapped-pin card need it. */
export interface MapEntry {
  pin: StaysMapPin | null;
  name: string;
  image?: string;
  href: string;
  search?: Record<string, unknown>;
  nearby?: string | null;
  typeLabel?: string;
  stars?: number;
  city?: string;
  /** Which saved list the heart writes to: camps are activities. */
  save?: SaveTarget;
}

function inArea(lat: number | null | undefined, lng: number | null | undefined, b: StaysMapBounds): boolean {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  return lat <= b.north && lat >= b.south && lng <= b.east && lng >= b.west;
}

/**
 * The Stays hub's "All" (owner, 2026-09-26): the camps from our own catalogue
 * and every LiteAPI property, hotels, riads, resorts, villas and guesthouses,
 * in one grid of the same cards. The explore-only hotel guides are not here;
 * the hub's activity filter asks for bookable rows only.
 *
 * With dates and a party in the URL (the Where / When / Who pill), the LiteAPI
 * half comes back priced for them, cheapest first; unpriced properties last.
 */
export function StaysHubGrid({
  camps = [],
  propertyType = ALL_STAY_TYPES,
  cityFilter,
  sort = "price_asc",
  citySlug,
  lang,
  search,
  mapOn = false,
  filters,
  vibes = [],
  onVibesChange,
  onMapChange,
  stickyBar,
  priceRange,
}: {
  /** Price per night from the Filters panel, in the guest's currency. */
  priceRange?: { min?: number; max?: number };
  /** Map/List from the pinned bar; the page owns the remembered state. */
  onMapChange?: (next: boolean) => void;
  /** The slim bar that pins under the rail once the page controls scroll away. */
  stickyBar?: { activeType: string; carriedSearch?: Record<string, unknown>; place?: string | null };
  /** The mood chips that are on. Several at once; a stay must match them all. */
  vibes?: StayVibe[];
  onVibesChange?: (next: StayVibe[]) => void;
  /** Stars, pool, breakfast, free cancellation from the Filters panel. */
  filters?: StayFilters;
  /** Camps from our own catalogue; only the "All" hub has them. */
  camps?: CampItem[];
  /** One property type (the Riads, Hotels... pages) or every type. */
  propertyType?: string;
  /** A type page narrows to a city on the client; the hub fetches per city. */
  cityFilter?: string;
  sort?: StaysCitySortOption;
  citySlug?: string;
  lang: string;
  search: { checkin?: string; checkout?: string; adults?: number | string; childAges?: string | number };
  /** List on the left, map on the right (a desk); the map alone (a phone). */
  mapOn?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const displayMad = useDisplayPriceParts();
  const adults = search.adults != null && search.adults !== "" ? Number(search.adults) : undefined;
  const childAges = useMemo(() => parseChildAges(search.childAges), [search.childAges]);
  const { data, isLoading } = useStaysByType(
    propertyType,
    { checkin: search.checkin, checkout: search.checkout, adults, childAges },
    // A type page reads the list its route loader already fetched (all
    // cities, same query key) and narrows it below.
    propertyType === ALL_STAY_TYPES ? citySlug : undefined,
  );
  const { data: destinations } = useStayDestinations();
  const cityName = useMemo(
    () => new Map((destinations?.destinations ?? []).map((d) => [d.slug ?? "", d.name])),
    [destinations],
  );
  const nightlyOf = useNightlyPrice(nightsBetween(search.checkin, search.checkout));
  const filterKey = JSON.stringify([filters ?? {}, priceRange?.min ?? null, priceRange?.max ?? null]);
  const filtering = countStayFilters(filters) > 0;
  const filteredProperties = useMemo(() => {
    const all = data?.properties ?? [];
    const sorted = sortStayProperties(cityFilter ? all.filter((p) => p.citySlug === cityFilter) : all, sort);
    return sorted.filter(
      (p) =>
        (!filtering || !filters || matchesStayFilters(p, filters)) &&
        inPriceRange(nightlyOf(p), priceRange?.min, priceRange?.max),
    );
    // filterKey stands in for the filters object, rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filterKey, filtering, cityFilter, sort, nightlyOf]);
  /* Camps carry no star rating or amenity list, so any stay filter leaves them
     out rather than letting them pass a test they were never given. */
  const campsFiltered = useMemo(() => (filtering ? [] : camps), [camps, filtering]);

  /* How many stays each mood would leave, from the list as filtered so far,
     so a chip never leads to an empty page without saying so first. */
  const vibeKey = vibes.join(",");
  /* A chip's count is what the list would hold with that mood added to the
     ones already on (moods narrow together), so a count never promises more
     than tapping it gives. An active chip shows the list as it stands. */
  const vibeCounts = useMemo(() => {
    const out = {} as Record<StayVibe, number>;
    for (const v of STAY_VIBES) {
      const next = vibes.includes(v) ? vibes : [...vibes, v];
      out[v] =
        filteredProperties.filter((p) => stayHasVibes(p.amenities, next)).length +
        (campHasVibes(next) ? campsFiltered.length : 0);
    }
    return out;
    // vibeKey stands in for the vibes array, rebuilt from the URL each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campsFiltered, filteredProperties, vibeKey]);
  const campsInScope = useMemo(
    () => (campHasVibes(vibes) ? campsFiltered : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campsFiltered, vibeKey],
  );

  /* "Search this area" narrows the list to what the map shows; the pins stay
     put so the guest can see what they left out. A new city or search drops it. */
  const allProperties = useMemo(
    () => (vibes.length ? filteredProperties.filter((p) => stayHasVibes(p.amenities, vibes)) : filteredProperties),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredProperties, vibeKey],
  );
  const [area, setArea] = useState<StaysMapBounds | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const properties = useMemo(
    () => (area ? allProperties.filter((p) => inArea(p.latitude, p.longitude, area)) : allProperties),
    [allProperties, area],
  );
  /*
   * While the hotels are being priced, the camps wait too. Their prices are
   * ours and arrive at once, so after a date change the page showed "2 stays"
   * (the camps) for seconds with no sign a search was running, then the
   * hotels appeared (owner, 2026-09-30: "they must show the same time").
   * Everything now lands together, and the searching state fills the wait.
   */
  const campsShown = useMemo(
    () =>
      isLoading ? [] : area ? campsInScope.filter((c) => inArea(c.latitude, c.longitude, area)) : campsInScope,
    [campsInScope, area, isLoading],
  );

  /* 24 cards at a time, camps included, with the same "You've seen X of Y"
     footer as every other hub; the next 24 load as the guest scrolls. A new city or search
     starts again from the top. */
  const [shown, setShown] = useState(PAGE);
  const resetKey = [citySlug, cityFilter, sort, search.checkin, search.checkout, search.adults, search.childAges, filterKey, vibeKey].join("|");
  useEffect(() => {
    setShown(PAGE);
    setArea(null);
    setPickedId(null);
  }, [resetKey]);
  useEffect(() => {
    if (!mapOn) {
      setArea(null);
      setPickedId(null);
    }
  }, [mapOn]);
  const total = campsShown.length + properties.length;
  const visibleCamps = campsShown.slice(0, shown);
  const visibleStays = properties.slice(0, Math.max(0, shown - campsShown.length));
  const seen = Math.min(shown, total);
  const moreRef = useRef<HTMLDivElement>(null);
  useLoadOnScroll(moreRef, {
    enabled: !isLoading && seen < total,
    step: seen,
    onLoad: () => setShown((n) => n + PAGE),
  });
  const datesSelected = !!(search.checkin && search.checkout && adults);
  const childAgesParam = childAges.length ? childAges.join(",") : undefined;
  const linkSearch = {
    ...(search.checkin ? { checkin: search.checkin } : {}),
    ...(search.checkout ? { checkout: search.checkout } : {}),
    ...(adults != null ? { adults } : {}),
    ...(childAgesParam ? { childAges: childAgesParam } : {}),
  };

  /** "About 6 min walk to Jemaa el-Fnaa", or null where no landmark applies. */
  const nearbyText = (citySlug: string, lat: number | null | undefined, lng: number | null | undefined) => {
    const n = nearbyLandmark(citySlug, lat, lng);
    if (!n) return null;
    return t(`stays.nearby.${n.mode}`, { minutes: n.minutes, place: t(`stays.landmarks.${n.key}`) });
  };
  /* Every stay, not only the 24 on screen: the map is how a guest finds the
     one further down. The pin carries the price the card prints. */
  const entries = useMemo(() => {
    const out = new Map<string, MapEntry>();
    // No pins while searching: the camps alone would be half the answer.
    if (!mapOn || isLoading) return out;
    for (const c of campsInScope) {
      const has = typeof c.latitude === "number" && typeof c.longitude === "number";
      const p = c.price > 0 ? displayMad(c.price) : null;
      out.set(c.id, {
        pin: has
          ? { id: c.id, lat: c.latitude as number, lng: c.longitude as number, label: p ? `${p.value} ${p.currency}` : "", icon: CAMP_ICON, title: c.title }
          : null,
        name: c.title,
        image: c.images[0],
        typeLabel: c.category?.name,
        city: c.city.name,
        save: {
          kind: "activity",
          item: { slug: c.slug, title: c.title, city: c.city.name, price: c.price, currency: "MAD", image: c.images[0], category: c.category?.name },
        },
        href: activityHrefString({ lang, citySlug: c.city.slug, categorySlug: c.category?.slug, segment: c.segment, slug: c.slug }),
      });
    }
    const nights =
      search.checkin && search.checkout
        ? Math.max(1, Math.round((Date.parse(`${search.checkout}T00:00:00Z`) - Date.parse(`${search.checkin}T00:00:00Z`)) / 86_400_000))
        : 1;
    for (const s of allProperties) {
      let label = "";
      if (s.price) {
        label = `${formatStayMoney(Math.round(s.price.sellTotal / nights), "", i18n.language)} ${currencyLabel(s.price.currency, i18n.language)}`;
      } else if (!datesSelected && s.fromPriceNightly && s.fromPriceCurrency) {
        if (s.fromPriceCurrency === "MAD") {
          const p = displayMad(s.fromPriceNightly);
          label = `${p.value} ${p.currency}`;
        } else {
          label = `${formatStayMoney(Math.round(s.fromPriceNightly), "", i18n.language)} ${currencyLabel(s.fromPriceCurrency, i18n.language)}`;
        }
      }
      const has = typeof s.latitude === "number" && typeof s.longitude === "number";
      out.set(s.id, {
        pin: has
          ? { id: s.id, lat: s.latitude as number, lng: s.longitude as number, label, icon: stayTypeIconSrc(s.propertyType), title: s.name }
          : null,
        name: s.name,
        image: s.images[0],
        typeLabel: t(`stays.type_single.${s.propertyType}`, { defaultValue: "" }),
        stars: s.starRating ?? 0,
        city: cityName.get(s.citySlug) ?? undefined,
        save: {
          kind: "stay",
          item: {
            id: s.id,
            slug: s.slug,
            citySlug: s.citySlug,
            name: s.name,
            cityName: cityName.get(s.citySlug) ?? undefined,
            image: s.images[0],
            propertyType: s.propertyType,
            starRating: s.starRating ?? undefined,
          },
        },
        href: `/${lang}/stays/${s.citySlug}/${s.slug}`,
        search: linkSearch,
        nearby: nearbyText(s.citySlug, s.latitude, s.longitude),
      });
    }
    return out;
    // linkSearch is rebuilt from the same inputs listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOn, isLoading, campsInScope, allProperties, displayMad, lang, i18n.language, datesSelected, search.checkin, search.checkout, adults, childAgesParam, cityName]);
  const pins = useMemo(
    () => [...entries.values()].flatMap((e) => (e.pin ? [e.pin] : [])),
    [entries],
  );

  /** A tapped pin brings its card on screen, past "Show more" if need be. */
  function pickFromMap(id: string | null) {
    setPickedId(id);
    if (!id || typeof window === "undefined" || !window.matchMedia("(min-width: 1024px)").matches) return;
    const campIdx = campsShown.findIndex((c) => c.id === id);
    const stayIdx = properties.findIndex((p) => p.id === id);
    const idx = campIdx >= 0 ? campIdx : stayIdx >= 0 ? campsShown.length + stayIdx : -1;
    if (idx < 0) return;
    if (idx >= shown) setShown(Math.ceil((idx + 1) / PAGE) * PAGE);
    requestAnimationFrame(() =>
      document.getElementById(`stay-hub-card-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  }


  const activeId = hoverId ?? pickedId;

  /* Phones get the full-screen map layer; a desk keeps the side-by-side. */
  const [isDesk, setIsDesk] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesk(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  // The map is one tap away on every Stays page: have it ready.
  useEffect(() => {
    if (onMapChange) preloadStaysMap();
  }, [onMapChange]);
  const phoneStays = useMemo(() => {
    const out = new Map<string, PhoneMapStay>();
    for (const [id, e] of entries) {
      if (!e.pin) continue;
      out.set(id, {
        id,
        name: e.name,
        image: e.image,
        href: e.href,
        search: e.search,
        typeLabel: e.typeLabel,
        stars: e.stars,
        city: e.city,
        save: e.save,
        nearby: e.nearby,
        price: e.pin.label || undefined,
        lat: e.pin.lat,
        lng: e.pin.lng,
      });
    }
    return out;
  }, [entries]);
  const searchSummary = useMemo(() => {
    const where = (cityFilter || citySlug ? cityName.get((cityFilter || citySlug) as string) : null) ?? t("explore.all_of_morocco");
    let when = t("stays.search.any_dates");
    if (search.checkin && search.checkout) {
      const f = new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { day: "numeric", month: "short" });
      when = `${f.format(new Date(`${search.checkin}T12:00:00`))} – ${f.format(new Date(`${search.checkout}T12:00:00`))}`;
    }
    return `${where} · ${when}`;
  }, [cityFilter, citySlug, cityName, search.checkin, search.checkout, lang, t]);
  /* The mood row; once it scrolls up under the rail, the pinned bar takes over. */
  const moodsRef = useRef<HTMLDivElement>(null);

  /*
   * A change made from the pinned bar (mood, filter, sort, Map/List) keeps the
   * guest down in the list with the bar still showing: the list starts again
   * right under the bar. Sending them to the top of the page read as the page
   * resetting, and the bar they had just used vanished (owner, 2026-09-28).
   * A change made up top, with the page's own controls in view, moves nothing.
   */
  const listRef = useRef<HTMLDivElement>(null);
  // On a phone the map is a layer over the list, which never changes layout,
  // so opening or closing it must not move the list.
  const layoutMap = mapOn && isDesk;
  const jumpKey = [filterKey, vibeKey, layoutMap ? "map" : "list", sort].join("|");
  const lastJumpKey = useRef(jumpKey);
  const lastMapOn = useRef(layoutMap);
  useEffect(() => {
    if (lastJumpKey.current === jumpKey) return;
    lastJumpKey.current = jumpKey;
    const mapOnly = lastMapOn.current !== layoutMap;
    lastMapOn.current = layoutMap;
    const list = listRef.current;
    const moods = moodsRef.current;
    if (!list || !moods) return;
    const line = barLine();
    if (moods.getBoundingClientRect().bottom < line) {
      const top = list.getBoundingClientRect().top + window.scrollY - line - 12;
      // `instant`, not smooth: the page's own scroll anchoring cancels a smooth
      // scroll the moment the list above changes height.
      // Held for two frames so the folded header does not read this as the
      // guest scrolling up and slide back over the list.
      const root = document.documentElement;
      root.dataset.hubScrollHold = "";
      window.scrollTo({ top: Math.max(0, top), behavior: "instant" as ScrollBehavior });
      requestAnimationFrame(() => requestAnimationFrame(() => delete root.dataset.hubScrollHold));
    }
    // A new set of results settles in card by card. The map toggle has its
    // own motion (the glide below), so it does not get this one too.
    if (!mapOnly) staggerIn(visibleCards(list));
  }, [jumpKey, layoutMap]);

  /*
   * List to map and back: the cards glide to their new places. Their old
   * places are read during render, while the DOM still shows the old layout,
   * and the glide starts before the browser paints the new one.
   */
  const flipBefore = useRef<Map<string, DOMRect> | null>(null);
  const flipMapOn = useRef(layoutMap);
  if (flipMapOn.current !== layoutMap) {
    flipMapOn.current = layoutMap;
    flipBefore.current = typeof window !== "undefined" ? measureCards(listRef.current) : null;
  }
  useLayoutEffect(() => {
    if (!flipBefore.current) return;
    glideCards(listRef.current, flipBefore.current);
    flipBefore.current = null;
  }, [layoutMap]);


  const picked = pickedId ? entries.get(pickedId) : undefined;
  const cardWrap = (id: string) => ({
    id: `stay-hub-card-${id}`,
    onMouseEnter: layoutMap ? () => setHoverId(id) : undefined,
    onMouseLeave: layoutMap ? () => setHoverId(null) : undefined,
    // Same corners as the card (CARD_RADIUS) so the map-hover ring sits on its
    // edge; at 26px it floated outside the card as a stray line.
    className: "h-full rounded-[22px]",
    style: {
      transition: `box-shadow 200ms ${STAYS_EASE}, transform 200ms ${STAYS_EASE}`,
      ...(layoutMap && activeId === id ? { boxShadow: `0 0 0 2px ${NAVY}`, transform: "translateY(-3px)" } : {}),
    },
  });

  return (
    <>
      {stickyBar && onVibesChange ? (
        <StaysStickyBar
          lang={lang}
          activeType={stickyBar.activeType}
          carriedSearch={stickyBar.carriedSearch}
          place={stickyBar.place}
          sentinelRef={moodsRef}
          vibes={vibes}
          vibeCounts={vibeCounts}
          onVibesChange={onVibesChange}
          map={onMapChange ? { on: mapOn, onChange: onMapChange } : undefined}
        />
      ) : null}
      {onVibesChange ? (
        <div
          ref={moodsRef}
          role="group"
          aria-label={t("stays.vibes.label")}
          // A sideways scroller clips vertically too, so it gets room above and
          // below for the chips' hairline ring and shadow (`pt-2`, `pb-4`),
          // handed back by the negative margins so the layout does not move.
          // Without it the ring's top edge was cut flat.
          className="-mx-4 -mt-2 mb-2 flex gap-2 overflow-x-auto px-4 pb-4 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {STAY_VIBES.map((v) => {
            const on = vibes.includes(v);
            const count = vibeCounts[v];
            if (!on && count === 0) return null;
            return (
              <button
                key={v}
                type="button"
                aria-pressed={on}
                onClick={() => onVibesChange(on ? vibes.filter((x) => x !== v) : [...vibes, v])}
                className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition-[transform,box-shadow,background,color] duration-200 [transition-timing-function:cubic-bezier(.2,.8,.2,1)] active:scale-[0.97]"
                style={
                  on
                    ? { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON }
                    : { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px rgba(17,14,57,0.1)` }
                }
              >
                <MoodGlyph icon={VIBE_ICON[v]} on={on} size={16} />
                {t(`stays.vibes.${v}`)}
                <span className="text-[12px] font-medium" style={{ opacity: 0.7 }}>{count}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {/* At least a screen tall while something narrows the list: a three-stay
          result must still leave room to scroll it up under the pinned bar,
          or the page clamps short and the bar has nowhere to stay. */}
      <div
        ref={listRef}
        className={layoutMap ? "mj-stays-split lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-6" : undefined}
        style={filtering || vibes.length > 0 ? { minHeight: "calc(100dvh / var(--mj-ui-zoom, 1))" } : undefined}
      >
      {/* On a phone the list stays in place under the full-screen map. */}
      <div>
      {area ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <p className="m-0 text-[14px] font-semibold" style={{ color: NAVY }}>
            {t("stays.hub_map.area_active")}
          </p>
          <button
            type="button"
            onClick={() => setArea(null)}
            className="min-h-6 text-[14px] font-semibold underline underline-offset-4"
            style={{ color: NAVY }}
          >
            {t("stays.hub_map.show_all")}
          </button>
        </div>
      ) : null}
      {!area && (filtering || vibes.length > 0) && !isLoading && total === 0 ? (
        <p className="m-0 py-10 text-center text-[14px]" style={{ color: META }}>
          {t("stays.hub_filters.empty")}
        </p>
      ) : null}
      {area && total === 0 ? (
        <p className="m-0 py-10 text-center text-[14px]" style={{ color: META }}>
          {t("stays.hub_map.empty_area")}
        </p>
      ) : null}
      <div
        className={
          layoutMap
            ? "grid grid-cols-2 gap-4"
            : "grid grid-cols-2 gap-x-2.5 gap-y-5 min-[400px]:gap-x-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4"
        }
      >
        {visibleCamps.map((a) => (
          <div key={a.id} {...cardWrap(a.id)}>
          <ActivityCardDiscover
            key={a.id}
            slug={a.slug}
            images={a.images}
            title={a.title}
            city={a.city.name}
            citySlug={a.city.slug}
            category={a.category?.name ?? ""}
            categorySlug={a.category?.slug}
            segment={a.segment ?? null}
            mode={a.mode}
            price={a.price}
            isNew={a.isNew === true}
            typePrice={a.typePrice}
            rating={a.rating}
          />
          </div>
        ))}
        {visibleStays.map((p) => (
          <div key={p.id} {...cardWrap(p.id)}>
          <StayCard
            property={p}
            lang={lang}
            cityName={cityName.get(p.citySlug)}
            typeLabel={t(`stays.type_single.${p.propertyType}`, { defaultValue: "" })}
            datesSelected={datesSelected && !data?.pricingUnavailable}
            search={{ checkin: search.checkin, checkout: search.checkout, adults, childAges: childAgesParam }}
          />
          </div>
        ))}
      </div>
      {!isLoading && total > 0 ? (
        <div className="mt-12 flex flex-col items-center gap-3">
          <p className="m-0 text-[14px]" style={{ color: META }}>
            {t("explore.seen_of", { seen, total, defaultValue: `You've seen ${seen} of ${total}` })}
          </p>
          <div className="h-[3px] w-[180px] overflow-hidden rounded-full" style={{ background: "#E3E5EE" }}>
            <div
              className="h-full w-full origin-left rounded-full"
              style={{
                background: NAVY,
                transform: `scaleX(${total ? seen / total : 0})`,
                transition: "transform 420ms cubic-bezier(.2,.8,.2,1)",
              }}
            />
          </div>
          {/* No button: the next 24 arrive as this comes into view. */}
          {seen < total ? <div ref={moreRef} className="h-px w-full" aria-hidden /> : null}
        </div>
      ) : null}
      {isLoading ? (
        <StaysSearching
          type={propertyType === ALL_STAY_TYPES ? "hotel" : propertyType}
          cityName={cityFilter || citySlug ? cityName.get((cityFilter || citySlug) as string) : null}
          checkin={search.checkin}
          checkout={search.checkout}
        />
      ) : null}
      </div>
      {mapOn && !isDesk ? (
        <>
          {/* No MapViewStyles here: the layer covers the page, and hiding the
              footer underneath would shorten it and move the guest's place. */}
          <StaysPhoneMap
            lang={lang}
            onClose={() => onMapChange?.(false)}
            pins={pins}
            stays={phoneStays}
            fitKey={[propertyType, citySlug ?? "", cityFilter ?? ""].join("|")}
            count={total}
            searching={
              isLoading ? (
                <StaysSearching
                  type={propertyType === ALL_STAY_TYPES ? "hotel" : propertyType}
                  cityName={cityFilter || citySlug ? cityName.get((cityFilter || citySlug) as string) : null}
                  checkin={search.checkin}
                  checkout={search.checkout}
                />
              ) : null
            }
            searchSummary={searchSummary}
            datesSelected={datesSelected}
            activeType={stickyBar?.activeType ?? propertyType}
            carriedSearch={stickyBar?.carriedSearch}
            vibes={vibes}
            vibeCounts={vibeCounts}
            onVibesChange={(next) => onVibesChange?.(next)}
          />
        </>
      ) : null}
      {mapOn && isDesk ? (
        // Sticky beside the list on a desk, clear of the header and tab rail.
        // On a phone it is the page, tall enough to browse, short enough to
        // leave the floating Filters / List bar off its pins.
        // Pinned under the folded rail and the stays bar: both publish their
        // heights, so the map's top edge never hides behind them.
        <div
          className="lg:sticky lg:self-start mj-stays-map-pin"
        >
          {/* Fills the screen below the rail and the bar, on a desk only. A
              stylesheet line, not a Tailwind class: the math has to read two
              CSS variables, which arbitrary classes mangle. */}
          {/* The site footer and the hub's reading sections (intro, about the
              city, FAQ...) go while the map is on, as map views do
              (owner, 2026-09-28): the page is the list and the map, nothing
              below them. This style exists only in map view, which only a
              visitor can turn on, so the server-rendered page Google reads
              always keeps the footer and its category and city links. */}
          <MapViewStyles />
          <StaysHubMapLazy
            pins={pins}
            fitKey={[propertyType, citySlug ?? "", cityFilter ?? ""].join("|")}
            activeId={activeId}
            onHover={setHoverId}
            onSelect={pickFromMap}
            onSearchArea={(b) => {
              setArea(b);
              setShown(PAGE);
            }}
            selectedId={picked ? pickedId : null}
            selected={
              picked ? (
                <PickedStay entry={picked} onClose={() => setPickedId(null)} />
              ) : null
            }
            className="mj-stays-map mj-map-enter h-[calc(100dvh-300px)] min-h-[420px]"
          />
        </div>
      ) : null}
      </div>
    </>
  );
}

/**
 * The clicked pin's card, floating just above the pin (option C, owner
 * 2026-10-04): the photo leads, carrying the type, stars and the nightly
 * "from" price, with the name, walk time and an arrow underneath. Same pewter
 * frame and save heart as the list cards. Clicking anywhere on it opens the
 * property; clicking the map closes it.
 */
export function PickedStay({ entry }: { entry: MapEntry; onClose?: () => void }) {
  const { t } = useTranslation();
  const stars = entry.stars ?? 0;
  const price = entry.pin?.label;
  return (
    <article className="mj-activity-card group relative w-[248px]" style={cardFrameStyle}>
      <div className="relative overflow-hidden p-1.5" style={pewterInner(CARD_FILL, CARD_RADIUS)}>
        <div
          className="relative h-[150px] w-full overflow-hidden rounded-[16px] bg-[#e9e9f1] bg-cover bg-center"
          style={entry.image ? { backgroundImage: `url(${entry.image})` } : undefined}
        >
          {entry.typeLabel || stars > 0 ? (
            <span className="pointer-events-none absolute start-2 top-2 inline-flex min-h-[24px] items-center gap-1.5 whitespace-nowrap rounded-full bg-white/95 px-2.5 text-[10px] font-bold uppercase leading-[14px] tracking-[0.08em] text-[#040033] shadow-[0_2px_8px_rgba(4,0,51,0.15)]">
              {entry.typeLabel}
              {stars > 0 ? (
                <span className="inline-flex gap-px" aria-label={t("stays.star_rating", { count: stars })}>
                  {Array.from({ length: stars }).map((_, i) => (
                    <Icon key={i} name="ri-star-fill" className="text-[9px] text-[#E5B265]" aria-hidden="true" />
                  ))}
                </span>
              ) : null}
            </span>
          ) : null}
          {entry.save ? <SaveHeart className="absolute end-2 top-2 z-20" target={entry.save} /> : null}
          {price ? (
            <span className="pointer-events-none absolute bottom-2 start-2 inline-flex items-baseline gap-1 whitespace-nowrap rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#5a5d77] shadow-[0_2px_8px_rgba(4,0,51,0.15)]">
              {t("activity.from")}
              <span className="text-[13px] font-extrabold text-[#040033]">{price}</span>
              {entry.save?.kind === "stay" ? <span>{t("stays.per_night")}</span> : null}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 px-1.5 pb-1 pt-2">
          <div className="min-w-0 flex-1">
            <Link
              to={entry.href as never}
              search={(entry.search ?? {}) as never}
              className="line-clamp-2 break-words text-[13.5px] font-bold leading-[1.3] text-[#040033] no-underline after:absolute after:inset-0 after:z-10 after:content-['']"
            >
              {entry.name}
            </Link>
            {entry.nearby ? (
              <span className="mt-0.5 flex items-center gap-1 text-[11.5px] font-medium leading-[1.3] text-[#5a5d77]">
                <Icon name="ri-map-pin-2-line" className="shrink-0 text-[12px]" aria-hidden="true" />
                <span className="min-w-0 line-clamp-2">{entry.nearby}</span>
              </span>
            ) : null}
          </div>
          <span
            aria-hidden="true"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
            style={{ background: NAVY }}
          >
            <Icon name="ri-arrow-right-line" className="text-[15px] rtl:rotate-180" />
          </span>
        </div>
      </div>
    </article>
  );
}
